import type { DataSyncExcelSheet } from '@bliks-io/contracts';
import i18next from '@/lib/i18n';
import { supportedLanguages } from '@/i18n/locales';

/**
 * Column definition for Excel templates
 * Maps internal field names to translation keys
 */
export interface ColumnDefinition {
  /** Internal field name used in parsed data */
  field: string;
  /** Translation key for the column header */
  translationKey: string;
  /** Column width in characters */
  width: number;
  /** A sheet without this column reads as one of blank cells instead of lacking a column. */
  optional?: true;
}

/**
 * Template type enum for type safety
 */
export type TemplateType =
  | 'departments'
  | 'users'
  | 'processes'
  | 'functions'
  | 'teams'
  | 'systems'
  | 'partnerContacts';

/**
 * Sheet-level unit of a template. Every TemplateType has a main data sheet;
 * functions/teams workbooks additionally carry a memberships sheet, which is
 * a data area of its own but never a downloadable template. The Excel data
 * source workbook has five sheets of its own.
 */
export type DataAreaType =
  | TemplateType
  | 'memberships'
  | 'dataSyncPeople'
  | 'dataSyncDepartments'
  | 'dataSyncTeams'
  | 'dataSyncRoles'
  | 'dataSyncSystems';

/** A key of `bulkOnboarding.excel.sheets`: a translated sheet name. */
export type SheetKey = TemplateType | 'instructions' | 'memberships' | 'dataSyncPeople';

/** The data area of each sheet of the Excel data source workbook. */
export const DATA_SYNC_SHEET_AREAS: Record<DataSyncExcelSheet, DataAreaType> = {
  departments: 'dataSyncDepartments',
  teams: 'dataSyncTeams',
  roles: 'dataSyncRoles',
  systems: 'dataSyncSystems',
  people: 'dataSyncPeople'
};

/**
 * The hidden last sheet of the data source download: one column per sheet,
 * headed by its key, listing the ids that download listed. Never localized,
 * so a file downloaded in one app language uploads in another.
 */
export const DATA_SYNC_DOWNLOADED_IDS_SHEET = '_bliks';

/** The sheet name of each data area; the data source structure sheets carry the setup's. */
const SHEET_KEY_OF_DATA_AREA: Record<DataAreaType, SheetKey> = {
  departments: 'departments',
  users: 'users',
  processes: 'processes',
  functions: 'functions',
  teams: 'teams',
  systems: 'systems',
  partnerContacts: 'partnerContacts',
  memberships: 'memberships',
  dataSyncPeople: 'dataSyncPeople',
  dataSyncDepartments: 'departments',
  dataSyncTeams: 'teams',
  dataSyncRoles: 'functions',
  dataSyncSystems: 'systems'
};

const USER_COLUMNS: ColumnDefinition[] = [
  {
    field: 'firstName',
    translationKey: 'bulkOnboarding.excel.columns.users.firstName',
    width: 20
  },
  { field: 'lastName', translationKey: 'bulkOnboarding.excel.columns.users.lastName', width: 20 },
  { field: 'email', translationKey: 'bulkOnboarding.excel.columns.users.email', width: 30 },
  {
    field: 'department',
    translationKey: 'bulkOnboarding.excel.columns.users.department',
    width: 25
  },
  { field: 'role', translationKey: 'bulkOnboarding.excel.columns.users.role', width: 25 },
  {
    field: 'roleDescription',
    translationKey: 'bulkOnboarding.excel.columns.users.roleDescription',
    width: 40
  }
];

const DATA_SYNC_ID: ColumnDefinition = {
  field: 'id',
  translationKey: 'bulkOnboarding.excel.columns.dataSync.id',
  width: 28
};
const DATA_SYNC_LEAD: ColumnDefinition = {
  field: 'lead',
  translationKey: 'bulkOnboarding.excel.columns.dataSync.lead',
  width: 30
};

/**
 * Column definitions for each data area
 * This is the single source of truth for all column mappings
 */
export const COLUMN_DEFINITIONS: Record<DataAreaType, ColumnDefinition[]> = {
  departments: [
    { field: 'name', translationKey: 'bulkOnboarding.excel.columns.departments.name', width: 30 },
    {
      field: 'abbreviation',
      translationKey: 'bulkOnboarding.excel.columns.departments.abbreviation',
      width: 20
    },
    {
      field: 'description',
      translationKey: 'bulkOnboarding.excel.columns.departments.description',
      width: 40
    },
    {
      field: 'parentAbbr',
      translationKey: 'bulkOnboarding.excel.columns.departments.parentAbbreviation',
      width: 25
    },
    {
      field: 'managerEmail',
      translationKey: 'bulkOnboarding.excel.columns.departments.managerEmail',
      width: 30
    }
  ],
  users: USER_COLUMNS,
  // The Excel data source round trip: the users layout behind the Bliks id,
  // without the Stellenbeschreibung the data source does not carry.
  dataSyncPeople: [
    DATA_SYNC_ID,
    ...USER_COLUMNS.filter(({ field }) => field !== 'roleDescription'),
    {
      field: 'location',
      translationKey: 'bulkOnboarding.excel.columns.dataSync.location',
      width: 25,
      // Files downloaded before the column existed lack it; a blank Standort never clears one.
      optional: true
    }
  ],
  // Every column the data source structure sheets carry is compared on upload.
  dataSyncDepartments: [
    DATA_SYNC_ID,
    { field: 'name', translationKey: 'bulkOnboarding.excel.columns.departments.name', width: 30 },
    {
      // The parent's name, unlike the setup's parent abbreviation.
      field: 'parent',
      translationKey: 'bulkOnboarding.excel.columns.dataSync.parentDepartment',
      width: 30
    },
    DATA_SYNC_LEAD
  ],
  dataSyncTeams: [
    DATA_SYNC_ID,
    { field: 'name', translationKey: 'bulkOnboarding.excel.columns.teams.name', width: 30 },
    DATA_SYNC_LEAD
  ],
  dataSyncRoles: [
    DATA_SYNC_ID,
    { field: 'name', translationKey: 'bulkOnboarding.excel.columns.functions.name', width: 30 },
    DATA_SYNC_LEAD
  ],
  dataSyncSystems: [
    DATA_SYNC_ID,
    { field: 'name', translationKey: 'bulkOnboarding.excel.columns.systems.name', width: 30 },
    {
      field: 'owner',
      translationKey: 'bulkOnboarding.excel.columns.dataSync.owner',
      width: 30
    }
  ],
  processes: [
    { field: 'name', translationKey: 'bulkOnboarding.excel.columns.processes.name', width: 40 },
    {
      field: 'description',
      translationKey: 'bulkOnboarding.excel.columns.processes.description',
      width: 60
    },
    {
      field: 'department',
      translationKey: 'bulkOnboarding.excel.columns.processes.department',
      width: 25
    },
    {
      // Required by the initial-setup wizard; the post-onboarding bulk page
      // ignores the column (its endpoint falls back to the dept manager).
      field: 'ownerEmail',
      translationKey: 'bulkOnboarding.excel.columns.processes.ownerEmail',
      width: 35
    },
    {
      // Optional on both surfaces; an empty cell keeps the dynamic
      // department-manager fallback.
      field: 'accountableEmail',
      translationKey: 'bulkOnboarding.excel.columns.processes.accountableEmail',
      width: 35
    }
  ],
  functions: [
    { field: 'name', translationKey: 'bulkOnboarding.excel.columns.functions.name', width: 30 },
    {
      field: 'description',
      translationKey: 'bulkOnboarding.excel.columns.functions.description',
      width: 40
    },
    {
      field: 'department',
      translationKey: 'bulkOnboarding.excel.columns.functions.department',
      width: 25
    },
    {
      field: 'leadEmail',
      translationKey: 'bulkOnboarding.excel.columns.functions.leadEmail',
      width: 30
    },
    { field: 'email', translationKey: 'bulkOnboarding.excel.columns.functions.email', width: 30 }
  ],
  teams: [
    { field: 'name', translationKey: 'bulkOnboarding.excel.columns.teams.name', width: 30 },
    {
      field: 'description',
      translationKey: 'bulkOnboarding.excel.columns.teams.description',
      width: 40
    },
    {
      field: 'department',
      translationKey: 'bulkOnboarding.excel.columns.teams.department',
      width: 25
    },
    {
      field: 'leadEmail',
      translationKey: 'bulkOnboarding.excel.columns.teams.leadEmail',
      width: 30
    },
    { field: 'email', translationKey: 'bulkOnboarding.excel.columns.teams.email', width: 30 }
  ],
  memberships: [
    {
      field: 'orgFunctionName',
      translationKey: 'bulkOnboarding.excel.columns.memberships.orgFunctionName',
      width: 30
    },
    {
      field: 'memberEmail',
      translationKey: 'bulkOnboarding.excel.columns.memberships.memberEmail',
      width: 30
    }
  ],
  systems: [
    { field: 'name', translationKey: 'bulkOnboarding.excel.columns.systems.name', width: 30 },
    {
      field: 'description',
      translationKey: 'bulkOnboarding.excel.columns.systems.description',
      width: 40
    },
    {
      field: 'category',
      translationKey: 'bulkOnboarding.excel.columns.systems.category',
      width: 20
    },
    { field: 'url', translationKey: 'bulkOnboarding.excel.columns.systems.url', width: 35 },
    {
      field: 'licenceSeats',
      translationKey: 'bulkOnboarding.excel.columns.systems.licenceSeats',
      width: 15
    },
    {
      field: 'ownerEmail',
      translationKey: 'bulkOnboarding.excel.columns.systems.ownerEmail',
      width: 30
    }
  ],
  partnerContacts: [
    {
      field: 'firstName',
      translationKey: 'bulkOnboarding.excel.columns.partnerContacts.firstName',
      width: 20
    },
    {
      field: 'lastName',
      translationKey: 'bulkOnboarding.excel.columns.partnerContacts.lastName',
      width: 20
    },
    {
      field: 'company',
      translationKey: 'bulkOnboarding.excel.columns.partnerContacts.company',
      width: 30
    },
    {
      field: 'email',
      translationKey: 'bulkOnboarding.excel.columns.partnerContacts.email',
      width: 30
    },
    {
      field: 'phone',
      translationKey: 'bulkOnboarding.excel.columns.partnerContacts.phone',
      width: 20
    },
    {
      field: 'note',
      translationKey: 'bulkOnboarding.excel.columns.partnerContacts.note',
      width: 40
    }
  ]
};

/**
 * Get translated column headers for a data area
 * Used when generating Excel templates
 */
export function getTranslatedHeaders(dataArea: DataAreaType): string[] {
  return COLUMN_DEFINITIONS[dataArea].map((col) => i18next.t(col.translationKey));
}

/**
 * Get column widths for a data area
 */
export function getColumnWidths(dataArea: DataAreaType): { wch: number }[] {
  return COLUMN_DEFINITIONS[dataArea].map((col) => ({ wch: col.width }));
}

/**
 * Get the field names for a data area in order
 */
export function getFieldNames(dataArea: DataAreaType): string[] {
  return COLUMN_DEFINITIONS[dataArea].map((col) => col.field);
}

/**
 * Header spellings from templates downloaded before the Stelle/Position
 * vocabulary rename — old files must keep importing instead of failing every
 * row with "position is required". Safe because no current header reuses
 * these words.
 */
const LEGACY_HEADERS: Record<string, string[]> = {
  role: ['Role*', 'Rolle*', 'Rôle*', 'Szerepkör*'],
  roleDescription: [
    'Role Description',
    'Rollenbeschreibung',
    'Description du rôle',
    'Szerepkör leírása'
  ]
};

/**
 * The lookup key of a sheet header: case- and required-marker-insensitive, so
 * a template whose column lost or gained its `*` keeps importing.
 */
export function headerKey(header: string): string {
  return header.replace(/\*/g, '').trim().toLowerCase();
}

/**
 * Build a reverse mapping from all possible header translations to field names
 * This is crucial for parsing files uploaded in any supported language
 *
 * @returns Map where keys are all possible translated headers (in all languages, as headerKey)
 *          and values are the internal field names
 */
export function buildHeaderToFieldMap(dataArea: DataAreaType): Map<string, string> {
  const map = new Map<string, string>();

  for (const col of COLUMN_DEFINITIONS[dataArea]) {
    for (const lang of supportedLanguages) {
      map.set(headerKey(i18next.t(col.translationKey, { lng: lang })), col.field);
    }
    for (const legacy of LEGACY_HEADERS[col.field] ?? []) {
      map.set(headerKey(legacy), col.field);
    }
  }

  return map;
}

/**
 * Get the translated sheet name for a data area
 */
export function getSheetName(dataArea: DataAreaType): string {
  return i18next.t(`bulkOnboarding.excel.sheets.${SHEET_KEY_OF_DATA_AREA[dataArea]}`);
}

/** The workbook's sheet that carries the data area's sheet name in any language, if any. */
export function findSheetName(sheetNames: string[], dataArea: DataAreaType): string | undefined {
  return sheetNames.find((name) => matchesSheetKey(name, SHEET_KEY_OF_DATA_AREA[dataArea]));
}

/**
 * Check whether a sheet name matches a sheet translation key in any language.
 * Uploads may come back in a different app locale than the template download.
 */
function matchesSheetKey(sheetName: string, sheetKey: SheetKey): boolean {
  for (const lang of supportedLanguages) {
    const translatedName = i18next.t(`bulkOnboarding.excel.sheets.${sheetKey}`, { lng: lang });
    if (sheetName.toLowerCase() === translatedName.toLowerCase()) {
      return true;
    }
  }
  return false;
}

/**
 * Role a sheet plays inside an uploaded workbook
 */
export type SheetKind = 'instructions' | 'memberships' | 'data';

/**
 * Classify a sheet by its translated name (in any language).
 * Anything that is neither instructions nor memberships counts as main data,
 * so files with renamed data sheets keep importing.
 */
export function classifySheet(sheetName: string): SheetKind {
  // Templates stopped shipping an instructions sheet, but workbooks downloaded
  // before that still contain one — this branch (and the
  // `sheets.instructions` translation keys behind it) must stay so those files
  // keep importing with the sheet skipped.
  if (matchesSheetKey(sheetName, 'instructions')) {
    return 'instructions';
  }
  if (matchesSheetKey(sheetName, 'memberships')) {
    return 'memberships';
  }
  return 'data';
}
